import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Play,
  Video,
  Search,
  Clock,
  Calendar,
  Eye,
  ExternalLink,
  Filter,
  Trash2
} from 'lucide-react';

const ReplaysPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  
  const [replays, setReplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [deletingId, setDeletingId] = useState(null);

  const eventTypes = [
    { value: 'all', label: 'Tous', icon: '📚' },
    { value: 'qa', label: 'Q&A', icon: '❓' },
    { value: 'analyse', label: 'Analyse', icon: '📊' },
    { value: 'juridique', label: 'Juridique', icon: '⚖️' },
    { value: 'financement', label: 'Financement', icon: '💰' },
    { value: 'recherche', label: 'Recherche', icon: '🔍' },
    { value: 'fiscalite', label: 'Fiscalité', icon: '📋' },
    { value: 'travaux', label: 'Travaux', icon: '🔧' },
    { value: 'gestion', label: 'Gestion locative', icon: '🏠' },
    { value: 'strategie', label: 'Stratégie', icon: '🎯' },
    { value: 'temoignage', label: 'Témoignages', icon: '🎤' },
    { value: 'autre', label: 'Autres', icon: '📹' }
  ];

  useEffect(() => {
    fetchReplays();
  }, []);

  const fetchReplays = async () => {
    try {
      const response = await api.get('/replays');
      setReplays(response.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReplay = async (e, replay) => {
    e.stopPropagation(); // Prevent card click
    
    if (!confirm(`Supprimer le replay "${replay.title}" ?`)) {
      return;
    }
    
    setDeletingId(replay.id);
    try {
      await api.delete(`/replays/${replay.id}`);
      setReplays(replays.filter(r => r.id !== replay.id));
      toast.success('Replay supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const handleWatchReplay = async (replay) => {
    try {
      // Increment view count
      await api.post(`/replays/${replay.id}/view`);
      
      // Update local count
      setReplays(replays.map(r => 
        r.id === replay.id ? { ...r, views: (r.views || 0) + 1 } : r
      ));
      
      // Open replay URL
      if (replay.replay_url) {
        window.open(replay.replay_url, '_blank');
      } else {
        toast.error('Lien du replay non disponible');
      }
    } catch (error) {
      // Still open the URL even if tracking fails
      if (replay.replay_url) {
        window.open(replay.replay_url, '_blank');
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const getEventTypeIcon = (type) => {
    const eventType = eventTypes.find(t => t.value === type);
    return eventType?.icon || '📹';
  };

  const filteredReplays = replays.filter(r => {
    const matchesType = selectedType === 'all' || r.event_type === selectedType;
    const matchesSearch = !searchQuery || 
      r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Video className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement des replays...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="replays-page">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                <Video className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Replays</h1>
                <p className="text-sm text-muted-foreground">
                  {replays.length} replay{replays.length !== 1 ? 's' : ''} disponible{replays.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un replay..."
              className="pl-10 bg-secondary/50"
              data-testid="search-replays-input"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {eventTypes.map((type) => (
              <Button
                key={type.value}
                variant={selectedType === type.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType(type.value)}
                className={`flex-shrink-0 ${selectedType === type.value ? 'bg-primary hover:bg-primary/90' : ''}`}
              >
                <span className="mr-1.5">{type.icon}</span>
                {type.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Replays Grid */}
        {filteredReplays.length === 0 ? (
          <Card className="card-base text-center py-12">
            <CardContent>
              <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl mb-2">Aucun replay trouvé</h3>
              <p className="text-muted-foreground">
                {searchQuery ? 'Essayez avec d\'autres mots-clés' : 'Les replays des événements apparaîtront ici'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence>
              {filteredReplays.map((replay, index) => (
                <motion.div
                  key={replay.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className="card-base group h-full overflow-hidden cursor-pointer hover:scale-[1.02] transition-all"
                    onClick={() => handleWatchReplay(replay)}
                    data-testid={`replay-${replay.id}`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-gradient-to-br from-red-500/20 to-red-500/5 flex items-center justify-center">
                      {replay.thumbnail_url ? (
                        <img 
                          src={replay.thumbnail_url} 
                          alt={replay.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-6xl">{getEventTypeIcon(replay.event_type)}</div>
                      )}
                      
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>

                      {/* Delete button (Admin only) */}
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 bg-black/50 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          onClick={(e) => handleDeleteReplay(e, replay)}
                          disabled={deletingId === replay.id}
                          data-testid={`delete-replay-${replay.id}`}
                        >
                          {deletingId === replay.id ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}

                      {/* Duration badge */}
                      {replay.duration && (
                        <Badge className="absolute bottom-2 right-2 bg-black/70 text-white border-0">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDuration(replay.duration)}
                        </Badge>
                      )}
                    </div>

                    <CardContent className="p-4">
                      <CardTitle className="text-base line-clamp-2 mb-2">
                        {replay.title}
                      </CardTitle>
                      
                      {replay.description && (
                        <CardDescription className="line-clamp-2 mb-3">
                          {replay.description}
                        </CardDescription>
                      )}

                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(replay.event_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {replay.views || 0} vue{(replay.views || 0) !== 1 ? 's' : ''}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mt-3">
                        <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-500/20">
                          {eventTypes.find(t => t.value === replay.event_type)?.label || 'Replay'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
};

export default ReplaysPage;
